import type { RewriteVariant, RuleContext } from '../types';
import { PLACEHOLDER, extractFacts } from './facts';

/**
 * About-section scaffolds.
 *
 * All three follow the same four-beat structure, because it is the one that
 * consistently survives being skimmed:
 *
 *   1. Hook — one specific line, inside the ~265 characters visible before
 *      "…see more".
 *   2. Proof — evidence, ideally the user's own quantified result.
 *   3. Toolkit — the searchable keywords, as prose rather than a bare list.
 *   4. Invitation — one explicit next step and a way to take it.
 *
 * What differs between variants is which beat leads and how much space it gets.
 * Bracketed tokens are placeholders (see `facts.ts`); nothing here is invented.
 */

export function buildAboutVariants(ctx: RuleContext): RewriteVariant[] {
  const facts = extractFacts(ctx);
  const { role, specialisation, callToAction } = facts;

  // The user's own best quantified bullet if they have one, so the proof
  // paragraph builds on something real instead of a placeholder.
  const proof = facts.strongestBullet
    ? `Most recently: ${facts.strongestBullet.replace(/\.$/, '')}.`
    : `A recent example: ${PLACEHOLDER.proof}.`;

  const toolkit =
    facts.topSkills.length > 0
      ? `Day to day I work with ${facts.topSkills.join(', ')}.`
      : `Day to day I work with ${PLACEHOLDER.specialisation}.`;

  const gaps =
    facts.missingKeywords.length > 0
      ? ` Worth mentioning if they apply to you: ${facts.missingKeywords.join(', ')}.`
      : '';

  return [
    {
      id: 'concise',
      label: 'Concise',
      description:
        'Short and scannable — around 500 characters. Best if you would rather be read in full than say everything.',
      text: [`I am a ${role} focused on ${specialisation}.`, '', proof, '', callToAction].join(
        '\n'
      ),
    },
    {
      id: 'keyword-rich',
      label: 'Keyword-rich',
      description:
        'Works the terms recruiters search for into full sentences. Use this if you are not appearing in searches.',
      text: [
        `${role} working in ${specialisation}. I care most about ${PLACEHOLDER.outcome} for ${PLACEHOLDER.audience}.`,
        '',
        proof,
        '',
        `${toolkit}${gaps}`,
        '',
        callToAction,
      ].join('\n'),
    },
    {
      id: 'impact-first',
      label: 'Impact-first',
      description:
        'Opens with the result, not the title. The strongest option when you have a number you are proud of.',
      text: [
        facts.strongestBullet
          ? `${facts.strongestBullet.replace(/\.$/, '')} — that is the kind of work I look for.`
          : `${PLACEHOLDER.proof} — that is the kind of work I look for.`,
        '',
        `I am a ${role}. I help ${PLACEHOLDER.audience} ${PLACEHOLDER.outcome}, and the part I enjoy most is ${PLACEHOLDER.outcome}.`,
        '',
        toolkit,
        '',
        callToAction,
      ].join('\n'),
    },
  ];
}
